import React from "react";
import { MenuCollapse } from "@/components/icons";
import { NavLink } from "react-router-dom";
import { NavItem } from "@/app/navigation";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

interface SidebarNavItemProps {
    item: NavItem;
    collapsed: boolean;
}

interface AppSidebarProps {
    collapsed: boolean;
    onToggleCollapsed: () => void;
    items: NavItem[];
}

// Icon column width — matches collapsed sidebar content area (76px - 16px ul padding = 60px).
// Icons are always centered in this column, so they never shift on collapse/expand.
const ICON_COL = "w-[60px]";

export function AppSidebarNavItem({ item, collapsed }: SidebarNavItemProps) {
    const { to, label, Icon, external } = item;

    // External link branch: same wrapper + inner content as NavLink, but using
    // a plain <a> so the browser does a real navigation out of the SPA.
    // Returned as a separate early-return so the NavLink branch stays
    // byte-identical to the pre-Console version (no Slot edge cases).
    if (external) {
        return (
            <li>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <a
                                href={to}
                                className={[
                                    "group relative flex items-center",
                                    "h-11 rounded-xl transition-colors w-full",
                                    "hover:bg-primary/10",
                                    "text-foreground/75",
                                ].join(" ")}
                            >
                                <div className={`flex-none ${ICON_COL} flex items-center justify-center`}>
                                    <Icon
                                        className={[
                                            "h-8 w-8 transition-colors",
                                            "text-foreground/75",
                                            "group-hover:text-primary",
                                        ].join(" ")}
                                    />
                                </div>
                                <span
                                    className={[
                                        "overflow-hidden whitespace-nowrap font-medium text-sm leading-7",
                                        "transition-[max-width,opacity] duration-200 ease-out",
                                        collapsed
                                            ? "max-w-0 opacity-0 pointer-events-none"
                                            : "max-w-[170px] opacity-100",
                                    ].join(" ")}
                                >
                                    {label}
                                </span>
                            </a>
                        </TooltipTrigger>
                        {collapsed && (
                            <TooltipContent
                                side="right"
                                hideArrow
                                sideOffset={8}
                                className="bg-card text-foreground border border-border shadow-md"
                            >
                                {label}
                            </TooltipContent>
                        )}
                    </Tooltip>
                </TooltipProvider>
            </li>
        );
    }

    return (
        <li>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <NavLink
                            to={to}
                            end={to === "/"}
                            className={({ isActive }) =>
                                [
                                    "group relative flex items-center",
                                    "h-11 rounded-xl transition-colors w-full",
                                    "hover:bg-primary/10",
                                    isActive ? "bg-primary/12 text-foreground" : "text-foreground/75",
                                ].join(" ")
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <div className={`flex-none ${ICON_COL} flex items-center justify-center`}>
                                        <Icon
                                            className={[
                                                "h-8 w-8 transition-colors",
                                                isActive ? "text-primary" : "text-foreground/75",
                                                "group-hover:text-primary",
                                            ].join(" ")}
                                        />
                                    </div>
                                    <span
                                        className={[
                                            "overflow-hidden whitespace-nowrap font-medium text-sm leading-7",
                                            "transition-[max-width,opacity] duration-200 ease-out",
                                            collapsed
                                                ? "max-w-0 opacity-0 pointer-events-none"
                                                : "max-w-[170px] opacity-100",
                                        ].join(" ")}
                                    >
                                        {label}
                                    </span>
                                </>
                            )}
                        </NavLink>
                    </TooltipTrigger>
                    {collapsed && (
                        <TooltipContent
                            side="right"
                            hideArrow
                            sideOffset={8}
                            className="bg-card text-foreground border border-border shadow-md"
                        >
                            {label}
                        </TooltipContent>
                    )}
                </Tooltip>
            </TooltipProvider>
        </li>
    );
}

export function Sidebar({ collapsed, onToggleCollapsed, items }: AppSidebarProps) {
    return (
        <aside className="w-full h-full flex flex-col border-r border-sidebar-border bg-sidebar sidebar">
            {/* Header — same px-2 + icon column as nav items so toggle button aligns with icons */}
            <div className="flex-shrink-0 px-2 pt-3 pb-2 border-b border-border/40">
                <div className="flex items-center h-11">
                    <div className={`flex-none ${ICON_COL} flex items-center justify-center`}>
                        <button
                            onClick={onToggleCollapsed}
                            className="h-9 w-9 rounded-full bg-background border border-border shadow-sm flex items-center justify-center hover:bg-muted transition-colors"
                        >
                            <MenuCollapse
                                className={`h-5 w-5 transition-transform duration-300 ease-in-out ${collapsed ? "rotate-180" : ""}`}
                            />
                        </button>
                    </div>
                    <div
                        className={[
                            "overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ease-out",
                            collapsed ? "max-w-0 opacity-0" : "max-w-[170px] opacity-100",
                        ].join(" ")}
                    >
                        <div className="font-semibold text-foreground leading-tight">Voltie</div>
                        <div className="text-xs text-foreground/60">EVSE Control Panel</div>
                    </div>
                </div>
            </div>

            {/* Nav — scrollable when viewport is short */}
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden sidebar-scroll">
                <ul className="flex flex-col gap-[clamp(0.125rem,2vh,1.75rem)] px-2 py-4">
                    {items.map((item) => (
                        <AppSidebarNavItem
                            key={item.to}
                            item={item}
                            collapsed={collapsed}
                        />
                    ))}
                </ul>
            </div>

            <div
                className={[
                    "flex-shrink-0 px-2 pb-3 overflow-hidden whitespace-nowrap transition-[max-height,opacity] duration-200 ease-out",
                    collapsed ? "max-h-0 opacity-0" : "max-h-8 opacity-100",
                ].join(" ")}
            >
                <div className="text-xs text-foreground/40 text-center">v0.2</div>
            </div>
        </aside>
    );
}
